/**
 * Group / ungroup / restructure persistence (checkpoint layer elements).
 *
 * Implemented routes:
 * - POST `/checkpoints/{spreadId}/layers/{layer}/elements/group`
 * - POST `/checkpoints/{spreadId}/layers/{layer}/elements/ungroup/{objectId}`
 * - POST `/checkpoints/{spreadId}/layers/{layer}/elements/restructure`
 *
 * `group`, `ungroup`, and `restructure` forward to these endpoints when `apiBody` is set on the
 * payload (Phase B). Otherwise they log once and no-op so existing callers stay safe.
 *
 * `reposition` sends the kind-7 row, non–kind-7 subtree shapes, and unlocked nested text in one batch:
 * `PUT …/elements/move|rotate`, `PUT …/elements/update` for **resize** (full blob replace so child
 * `path` / kind-specific fields match canvas geometry), or `PUT …/elements/update` when a full
 * multi-field update is required (e.g. effects + geometry).
 *
 * Child geometry in `apiBody` values must be **canvas-absolute** (see `group-elements-api.ts`).
 *
 * `restructure` sends `apiBody` when the editor provides it (e.g. wrap payload: survivor nested
 * kind-7 row + flat `elements`). Parent-change-only `moves` without `apiBody` still no-op.
 */

import type { ElementLayer, StowedElement } from '@/api/types/stowed-element'
import { type ElementWriteOptions, elementsService } from '@/api/services/elements.service'
import type {
  GroupElementsRequest,
  RestructureElementsRequest,
  UngroupElementsRequest,
  UngroupElementsResponse,
} from '@/api/types/group-elements-api'
import { httpClient } from '@/api/http/client'
import { logger } from '@/utils/logger'
import type { GroupCommandPayload } from '@/api/types/group-commands'
import type { UngroupCommandPayload } from '@/api/types/group-commands'
import type { RestructureCommandPayload } from '@/api/types/group-commands'
import type { GroupGeometricPersistOp } from '@/api/types/group-commands'
import type { RepositionCommandPayload } from '@/api/types/group-commands'

export type { ElementWriteOptions } from '@/api/services/elements.service'

function toRequestConfig(options?: ElementWriteOptions): { keepalive?: boolean } | undefined {
  return options?.keepalive ? { keepalive: true } : undefined
}

/** Map a canvas transform gesture to the group reposition persist route. */
export function groupGeometricPersistOpFromTransformSource(
  source: 'move' | 'resize' | 'rotate' | 'align' | 'update'
): GroupGeometricPersistOp {
  if (source === 'rotate') return 'rotate'
  if (source === 'move' || source === 'resize') return 'update'
  if (source === 'align') return 'move'
  return 'update'
}

export type {
  GroupElementsRequest,
  RestructureElementsRequest,
  UngroupElementsRequest,
  UngroupElementsResponse,
}

function enc(value: string): string {
  return encodeURIComponent(value.trim())
}

function elementsBasePath(spreadId: string, layer: ElementLayer): string {
  return `/checkpoints/${enc(spreadId)}/layers/${enc(layer)}/elements`
}

/** POST …/elements/group */
export async function postGroupElements(
  spreadId: string,
  layer: ElementLayer,
  body: GroupElementsRequest,
  options?: ElementWriteOptions
): Promise<unknown> {
  return await httpClient.post<GroupElementsRequest, unknown>(
    `${elementsBasePath(spreadId, layer)}/group`,
    body,
    toRequestConfig(options)
  )
}

/** POST …/elements/ungroup/{groupObjectId} */
export async function postUngroupElements(
  spreadId: string,
  layer: ElementLayer,
  groupObjectId: string,
  body: UngroupElementsRequest,
  options?: ElementWriteOptions
): Promise<UngroupElementsResponse> {
  return await httpClient.post<UngroupElementsRequest, UngroupElementsResponse>(
    `${elementsBasePath(spreadId, layer)}/ungroup/${enc(groupObjectId)}`,
    body,
    toRequestConfig(options)
  )
}

/** POST …/elements/restructure */
export async function postRestructureElements(
  spreadId: string,
  layer: ElementLayer,
  body: RestructureElementsRequest,
  options?: ElementWriteOptions
): Promise<unknown> {
  return await httpClient.post<StowedElement[], unknown>(
    `${elementsBasePath(spreadId, layer)}/restructure`,
    body,
    toRequestConfig(options)
  )
}

async function group(payload: GroupCommandPayload, options?: ElementWriteOptions): Promise<void> {
  if (payload.apiBody) {
    await postGroupElements(payload.spreadId, payload.layer, payload.apiBody, options)
    return
  }

  logger.debug('groupActionsService.group (no apiBody — not sent to API)', payload)
}

async function ungroup(
  payload: UngroupCommandPayload,
  options?: ElementWriteOptions
): Promise<void> {
  if (payload.apiBody && payload.apiBody.length > 0) {
    await postUngroupElements(
      payload.spreadId,
      payload.layer,
      payload.groupId,
      payload.apiBody,
      options
    )
    return
  }

  logger.debug('groupActionsService.ungroup (no apiBody — not sent to API)', payload)
}

async function restructure(
  payload: RestructureCommandPayload,
  options?: ElementWriteOptions
): Promise<void> {
  if (payload.apiBody && payload.apiBody.length > 0) {
    await postRestructureElements(payload.spreadId, payload.layer, payload.apiBody, options)
    return
  }

  logger.debug('groupActionsService.restructure (no apiBody — not sent to API)', payload)
}

/**
 * PUT …/elements/update validates each row has a non-empty `fractionalIndex` and that values are
 * strictly ascending in JSON array order (Go `ValidateElementFractionalIndexes`). Our group
 * persist batch intentionally lists the kind-7 row first, then descendants in canvas list order,
 * which can violate that ordering — sort before send.
 */
function sortStowedForCheckpointUpdate(batch: StowedElement[]): StowedElement[] {
  const fiKey = (row: StowedElement): string =>
    typeof row.fractionalIndex === 'string' ? row.fractionalIndex.trim() : ''

  return [...batch].sort((a, b) => {
    const fa = fiKey(a)
    const fb = fiKey(b)

    if (fa < fb) return -1
    if (fa > fb) return 1
    const ida = typeof a.objectId === 'string' ? a.objectId : ''
    const idb = typeof b.objectId === 'string' ? b.objectId : ''

    if (ida < idb) return -1
    if (ida > idb) return 1
    return 0
  })
}

async function reposition(
  payload: RepositionCommandPayload,
  options?: ElementWriteOptions
): Promise<void> {
  const { spreadId, layer, stowedBatch, geometricOperation } = payload

  if (stowedBatch.length === 0) {
    logger.warn('groupActionsService.reposition: empty stowedBatch', payload)
    return
  }

  switch (geometricOperation) {
    case 'move':
      await elementsService.moveElement(spreadId, layer, stowedBatch, options)
      return
    case 'resize':
    case 'update':
      // Group move/resize sends the kind-7 row plus canvas-absolute descendants. Full
      // `update` replaces each subtree blob (path, visible*, etc.) so reload matches the editor.
      await elementsService.updateElements(
        spreadId,
        layer,
        sortStowedForCheckpointUpdate(stowedBatch),
        undefined,
        options
      )
      return
    case 'rotate':
      await elementsService.rotateElement(spreadId, layer, stowedBatch, options)
      return
  }
}

export const groupActionsService = {
  group,
  ungroup,
  restructure,
  reposition,
  postGroupElements,
  postUngroupElements,
  postRestructureElements,
}
